/*
  Product page (sections/product.liquid): size buttons, quantity − / +,
  and ADD TO ORDER without leaving the page.

  - Picking a size finds the matching variant: updates the hidden variant
    id, the price box, the button (or SOLD OUT), which other sizes are
    crossed out, and the ?variant= in the address bar.
  - ADD TO ORDER posts to /cart/add.js, then fires `zp:cart-change` so the
    header's count and total update (with a pop). The mini cart stays
    closed (Nick: no unnecessary clicking out of it).
  - Photo zoom with a mouse: magnifier − / + (shown while hovering the
    photo) or a click on the photo, then drag to look around. No zoom from
    just hovering.
*/
(() => {
  const product = document.querySelector('[data-zp-product]');
  if (!product) return;

  const form = product.querySelector('[data-zp-buy]');
  const variants = JSON.parse(product.querySelector('[data-zp-variants]').textContent);
  const idInput = form.querySelector('[data-zp-variant-id]');
  const price = product.querySelector('[data-zp-price]');
  const add = form.querySelector('[data-zp-add]');
  const addLabel = add.querySelector('[data-zp-add-label]');
  const error = form.querySelector('[data-zp-buy-error]');
  const optionGroups = [...form.querySelectorAll('[data-zp-option]')];

  const matches = (variant, values) => variant.options.every((value, index) => value === values[index]);
  const selectedValues = () => optionGroups.map((group) => group.querySelector('input:checked')?.value);

  const setButton = (variant) => {
    if (!variant) {
      add.disabled = true;
      addLabel.textContent = add.dataset.labelUnavailable;
    } else {
      add.disabled = !variant.available;
      addLabel.textContent = variant.available ? add.dataset.labelAdd : add.dataset.labelSoldOut;
    }
  };

  const update = () => {
    const values = selectedValues();
    const variant = variants.find((item) => matches(item, values));
    setButton(variant);
    error.hidden = true;
    if (!variant) return;

    idInput.value = variant.id;
    price.textContent = variant.price;

    // Cross out every value that would land on a sold-out (or missing) variant, given the other choices.
    optionGroups.forEach((group, index) => {
      group.querySelectorAll('input').forEach((input) => {
        const test = values.slice();
        test[index] = input.value;
        const option = variants.find((item) => matches(item, test));
        input.toggleAttribute('data-sold-out', !option || !option.available);
      });
    });

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState(null, '', url);
  };

  // Photo zoom (mouse / trackpad only; phones keep pinch-zoom). Nothing happens on hover:
  // − / + step through 1×, 2×, 3×, and a zoomed photo can be dragged around inside its box.
  const media = product.querySelector('.zp-product__media');
  if (media && media.querySelector('[data-zp-photo-zoom]')) {
    const levels = [1, 2, 3];
    let level = 0;
    let panX = 0;
    let panY = 0;
    const zoomOut = media.querySelector('[data-zp-photo-zoom="-1"]');
    const zoomIn = media.querySelector('[data-zp-photo-zoom="1"]');

    // The photo currently in view (the track scrolls one photo per width).
    const currentImage = () => {
      const track = media.querySelector('.zp-product__track');
      const slides = media.querySelectorAll('.zp-product__slide');
      const index = track ? Math.round(track.scrollLeft / Math.max(track.clientWidth, 1)) : 0;
      return slides[index] && slides[index].querySelector('.zp-product__img');
    };

    // Keep the zoomed photo covering its box: it can't be dragged past its own edges.
    const clampPan = () => {
      const image = currentImage();
      const scale = levels[level];
      const maxX = image ? ((scale - 1) * image.clientWidth) / 2 : 0;
      const maxY = image ? ((scale - 1) * image.clientHeight) / 2 : 0;
      panX = Math.max(-maxX, Math.min(maxX, panX));
      panY = Math.max(-maxY, Math.min(maxY, panY));
    };

    const apply = () => {
      clampPan();
      media.style.setProperty('--zp-zoom', String(levels[level]));
      media.style.setProperty('--zp-pan-x', `${panX}px`);
      media.style.setProperty('--zp-pan-y', `${panY}px`);
      media.toggleAttribute('data-zoomed', level > 0);
      zoomOut.disabled = level === 0;
      zoomIn.disabled = level === levels.length - 1;
    };

    const reset = () => {
      level = 0;
      panX = 0;
      panY = 0;
      apply();
    };

    media.addEventListener('click', (event) => {
      const step = event.target.closest('[data-zp-photo-zoom]');
      if (step) {
        const before = levels[level];
        level = Math.max(0, Math.min(levels.length - 1, level + Number(step.dataset.zpPhotoZoom)));
        // Keep looking at the same spot while the zoom changes.
        const ratio = levels[level] / before;
        panX *= ratio;
        panY *= ratio;
        apply();
        return;
      }
      // Flipping to another photo starts it unzoomed.
      if (event.target.closest('.zp-product__arrow')) {
        reset();
        return;
      }
      // A plain click on the photo (not the end of a drag) zooms in one step toward that spot.
      if (moved || event.target.closest('button') || level === levels.length - 1) return;
      const image = currentImage();
      if (!image) return;
      const box = image.getBoundingClientRect();
      const before = levels[level];
      level += 1;
      const ratio = levels[level] / before;
      // Shift so the clicked point stays under the pointer.
      const offsetX = event.clientX - (box.left + box.width / 2);
      const offsetY = event.clientY - (box.top + box.height / 2);
      panX -= offsetX * (ratio - 1);
      panY -= offsetY * (ratio - 1);
      apply();
    });

    // Drag to look around while zoomed. A drag that moved isn't treated as a click.
    let drag = null;
    let moved = false;
    media.addEventListener('pointerdown', (event) => {
      moved = false;
      if (level === 0 || event.button !== 0 || event.target.closest('button')) return;
      event.preventDefault();
      drag = { x: event.clientX - panX, y: event.clientY - panY, startX: event.clientX, startY: event.clientY, id: event.pointerId };
      media.setPointerCapture(event.pointerId);
      media.setAttribute('data-dragging', '');
    });
    media.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      if (Math.abs(event.clientX - drag.startX) + Math.abs(event.clientY - drag.startY) > 4) moved = true;
      panX = event.clientX - drag.x;
      panY = event.clientY - drag.y;
      apply();
    });
    const endDrag = (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      drag = null;
      media.removeAttribute('data-dragging');
    };
    media.addEventListener('pointerup', endDrag);
    media.addEventListener('pointercancel', endDrag);
    window.addEventListener('resize', apply);
  }

  form.addEventListener('change', (event) => {
    if (event.target.matches('.zp-options__input')) update();
  });

  // Quantity − / +, never below 1.
  form.addEventListener('click', (event) => {
    const step = event.target.closest('[data-zp-qty-step]');
    if (!step) return;
    const input = form.querySelector('[name="quantity"]');
    const next = (parseInt(input.value, 10) || 1) + Number(step.dataset.zpQtyStep);
    input.value = String(Math.max(1, next));
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (add.disabled) return;
    error.hidden = true;
    add.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${form.getAttribute('action')}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || '');
      // No pop-up: the header's count and total turn red and pop, and the button says ADDED for a moment.
      document.dispatchEvent(new CustomEvent('zp:cart-change', { detail: { bump: true } }));
      addLabel.textContent = add.dataset.labelAdded;
      setTimeout(() => {
        if (!add.disabled) addLabel.textContent = add.dataset.labelAdd;
      }, 1600);
    } catch (problem) {
      error.textContent = problem.message || error.dataset.fallback || 'TRY AGAIN';
      error.hidden = false;
    } finally {
      add.removeAttribute('aria-busy');
    }
  });
})();
